import { DEFAULT_CLOSE_LABEL } from '@federa/const/aria-default-labels';
import { ICON_FOLDER, ICON_INBOX } from '@federa/const/icon-values';
import { LABEL_EXPLORE, LABEL_LEDGER, LABEL_PHONE, LABEL_SAVE_PROPERTY, LABEL_UNSAVE_PROPERTY } from '@federa/const/label-values';
import { spacing } from '../css-refs.spacing.ts';
import type { SemanticIconMeta, SemanticIconName } from '@federa/types/ui-icons';

const ARIA_LABEL_GO_BACK = 'Go back';

/** The accessible name of the collapse affordance, said once for both glyphs that render it: the `collapse` token and
 *  the `chevron-up` token point at ONE control, so a name that drifted between them would announce two controls to a
 *  screen reader where the sighted user sees one. File-local like {@link ARIA_LABEL_GO_BACK} above, which records the
 *  same two-rows-of-one-table shape; `const/aria-label-values.ts` homes names spelled at surfaces that never see each
 *  other, and these two rows sit in this table. */
const ARIA_LABEL_COLLAPSE = 'Collapse';

/** The accessible name of the busy state, said once for both glyphs that render it (`loading` and `spinner`) -- the
 *  same two-rows-of-one-table shape as {@link ARIA_LABEL_COLLAPSE}. */
const ARIA_LABEL_LOADING = 'Loading';

/**
 * THE ICON PAINT THAT DEFERS TO THE TEXT AROUND IT. `currentColor` is the CSS/SVG keyword for "whatever `color` this
 * element inherited", so an icon wearing it needs no palette entry of its own and can never disagree with the label it
 * sits beside. Three sites encode that one decision -- the stroke/fill wrappers' default `color` prop
 * (`IconWrapper.tsx`) and the `inherit` row of `SemanticIcon`'s color map -- and a copy that drifted would silently
 * pin an icon to a fixed ink while its text moved with the theme.
 *
 * Homed HERE, the icons' own presentation-token leaf, rather than in `const/svg-attributes.ts`: that leaf's exports
 * are read by `QUAL-NO-INLINE-UI-LITERALS-001`, and `currentColor` is still hand-spelled in a dozen leaves outside
 * this package, so exporting it there would convict files this fold does not touch.
 */
export const INHERITED_ICON_COLOR = 'currentColor';

/** Per-icon ARIA + presentation metadata. The `Record<SemanticIconName, ...>` type makes widening the union without updating this table a typecheck failure. */
const SEMANTIC_ICON_METAS: Record<SemanticIconName, SemanticIconMeta> = {
	// Primary actions
	submit: { icon: 'submit', ariaLabel: 'Submit' },
	check: { icon: 'check', ariaLabel: 'Check' },
	add: { icon: 'add', ariaLabel: 'Add' },

	// Secondary actions
	edit: { icon: 'edit', ariaLabel: 'Edit' },
	cancel: { icon: 'cancel', ariaLabel: 'Cancel' },
	close: { icon: 'close', ariaLabel: DEFAULT_CLOSE_LABEL },
	back: { icon: 'back', ariaLabel: ARIA_LABEL_GO_BACK },
	'arrow-back': { icon: 'arrow-back', ariaLabel: ARIA_LABEL_GO_BACK },
	forward: { icon: 'forward', ariaLabel: 'Go forward' },

	// Destructive actions
	delete: { icon: 'delete', ariaLabel: 'Delete', color: 'error' },
	remove: { icon: 'remove', ariaLabel: 'Remove', color: 'error' },

	// Info/Help
	info: { icon: 'info', ariaLabel: 'More information' },
	warning: { icon: 'warning', ariaLabel: 'Warning' },
	alert: { icon: 'alert', ariaLabel: 'Alert' },
	bell: { icon: 'bell', ariaLabel: 'Notifications' },

	// Sharing/Export
	share: { icon: 'share', ariaLabel: 'Share' },
	download: { icon: 'download', ariaLabel: 'Download' },
	upload: { icon: 'upload', ariaLabel: 'Upload' },
	copy: { icon: 'copy', ariaLabel: 'Copy to clipboard' },
	link: { icon: 'link', ariaLabel: 'Copy link' },

	// Navigation
	next: { icon: 'next', ariaLabel: 'Next' },
	previous: { icon: 'previous', ariaLabel: 'Previous' },
	expand: { icon: 'expand', ariaLabel: 'Expand' },
	collapse: { icon: 'collapse', ariaLabel: ARIA_LABEL_COLLAPSE },
	'chevron-up': { icon: 'chevron-up', ariaLabel: ARIA_LABEL_COLLAPSE },

	// Content actions
	search: { icon: 'search', ariaLabel: 'Search' },
	filter: { icon: 'filter', ariaLabel: 'Filter' },
	refresh: { icon: 'refresh', ariaLabel: 'Refresh' },
	moreVertical: { icon: 'moreVertical', ariaLabel: 'More options' },
	menu: { icon: 'menu', ariaLabel: 'Menu' },

	// User/Account
	settings: { icon: 'settings', ariaLabel: 'Settings' },

	// Property-specific
	calendar: { icon: 'calendar', ariaLabel: 'Schedule' },
	bookmark: { icon: 'bookmark', ariaLabel: LABEL_SAVE_PROPERTY },
	'bookmark-solid': {
		icon: 'bookmark-solid',
		fill: true,
		ariaLabel: LABEL_UNSAVE_PROPERTY,
	},
	map: { icon: 'map', ariaLabel: 'View on map' },
	compass: { icon: 'compass', ariaLabel: LABEL_EXPLORE },
	clock: { icon: 'clock', ariaLabel: 'Time' },
	home: { icon: 'home', ariaLabel: 'Home' },

	// Media
	pause: { icon: 'pause', ariaLabel: 'Pause' },
	play: { icon: 'play', ariaLabel: 'Play' },
	image: { icon: 'image', ariaLabel: 'Image' },
	file: { icon: 'file', ariaLabel: 'File' },
	document: { icon: 'document', ariaLabel: 'Document' },

	// Status indicators
	success: { icon: 'success', color: 'success', ariaLabel: 'Success' },
	error: { icon: 'error', color: 'error', ariaLabel: 'Error' },
	loading: { icon: 'loading', spin: true, ariaLabel: ARIA_LABEL_LOADING },
	spinner: { icon: 'spinner', spin: true, ariaLabel: ARIA_LABEL_LOADING },
	pending: { icon: 'pending', ariaLabel: 'Pending' },

	// Toggle states
	view: { icon: 'view', ariaLabel: 'View' },
	locked: { icon: 'locked', ariaLabel: 'Locked' },

	phone: { icon: 'phone', ariaLabel: LABEL_PHONE },
	chat: { icon: 'chat', ariaLabel: 'Chat' },
	send: { icon: 'send', ariaLabel: 'Send' },

	// Data/Analytics
	chart: { icon: 'chart', ariaLabel: 'Chart' },
	analytics: { icon: 'analytics', ariaLabel: 'Analytics' },
	trending: { icon: 'trending', ariaLabel: 'Trending up' },
	list: { icon: 'list', ariaLabel: 'List view' },
	grid: { icon: 'grid', ariaLabel: 'Grid view' },
	bolt: { icon: 'bolt', ariaLabel: 'Quick action' },
	sparkles: { icon: 'sparkles', ariaLabel: 'New feature' },
	star: { icon: 'star', ariaLabel: 'Featured' },

	// Content containers
	inbox: { icon: ICON_INBOX, ariaLabel: 'Inbox' },

	// Network
	network: { icon: 'network', ariaLabel: 'Network' },
	users: { icon: 'users', ariaLabel: 'Users' },
	user: { icon: 'user', ariaLabel: 'User' },
	account: { icon: 'account', ariaLabel: 'Account' },
	ledger: { icon: 'ledger', ariaLabel: LABEL_LEDGER },
	folder: { icon: ICON_FOLDER, ariaLabel: 'Folder' },

	// Property facts -- each labels a row in the property details tabs, so the aria label names the FACT.
	bedroom: { icon: 'bedroom', ariaLabel: 'Bedrooms' },
	bathroom: { icon: 'bathroom', ariaLabel: 'Bathrooms' },
	measurement: { icon: 'measurement', ariaLabel: 'Measurement' },
	stories: { icon: 'stories', ariaLabel: 'Stories' },
	garage: { icon: 'garage', ariaLabel: 'Garage' },
	'architectural-style': { icon: 'architectural-style', ariaLabel: 'Architectural style' },
	heating: { icon: 'heating', ariaLabel: 'Heating' },
	cooling: { icon: 'cooling', ariaLabel: 'Cooling' },
	'water-feature': { icon: 'water-feature', ariaLabel: 'Water feature' },
	'water-supply': { icon: 'water-supply', ariaLabel: 'Water supply' },
	sewer: { icon: 'sewer', ariaLabel: 'Sewer' },
	electric: { icon: 'electric', ariaLabel: 'Electric' },
	'below-grade': { icon: 'below-grade', ariaLabel: 'Below grade' },
	'above-grade': { icon: 'above-grade', ariaLabel: 'Above grade' },
	lot: { icon: 'lot', ariaLabel: 'Lot' },
	topography: { icon: 'topography', ariaLabel: 'Topography' },
	fee: { icon: 'fee', ariaLabel: 'Fee' },
	school: { icon: 'school', ariaLabel: 'School' },
};

/** Semantic action system mapping user intents to consistent icons and accessibility labels; every action should use these tokens. */
export const semanticActions = {
	icons: SEMANTIC_ICON_METAS,
} as const;

export const iconSizes = {
	xs: spacing[3],
	sm: spacing[4],
	md: spacing[5], // Default
	lg: spacing[6],
	xl: spacing[8],
	'2xl': spacing[10],
	'3xl': spacing[12],
	'4xl': spacing[16],

	// Context-specific
	tooltip: spacing[4],
	toast: spacing[4],
	button: spacing[5],
	navigation: spacing[6],
	hero: spacing[12],
} as const;
